import React, { useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { getLabaRugi } from '../../api';

export default function LabaRugi() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tglMulai, setTglMulai] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10);
  });
  const [tglSelesai, setTglSelesai] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    setLoading(true);
    getLabaRugi({ tanggal_mulai: tglMulai, tanggal_selesai: tglSelesai })
      .then(res => setData(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [tglMulai, tglSelesai]);

  const rupiah = (val) => {
    const n = Number(val);
    return isNaN(n) ? 'Rp 0' : `Rp ${n.toLocaleString('id-ID')}`;
  };

  // ===== PDF Export =====
  const generatePDF = () => {
    if (!data) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentW = pageW - 2 * margin;

    const primary = [17, 205, 239];
    const primaryDark = [0, 150, 180];
    const dark = [23, 43, 77];
    const gray = [136, 152, 170];
    const lightGray = [248, 249, 252];
    const white = [255, 255, 255];
    const isProfit = (data.laba_rugi || 0) >= 0;

    // ===== HEADER =====
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageW, 6, 'F');
    doc.setFillColor(...primaryDark);
    doc.rect(0, 6, pageW, 2, 'F');

    doc.setFillColor(...dark);
    doc.roundedRect(margin, 16, 50, 30, 4, 4, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('L Synexa', margin + 25, 30, { align: 'center' });
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Sistem Informasi Armada', margin + 25, 37, { align: 'center' });

    doc.setTextColor(...dark);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('LABA RUGI', margin + 58, 25);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text('Laporan Pendapatan dan Beban', margin + 58, 33);
    doc.text(`Periode: ${tglMulai} s.d ${tglSelesai}`, margin + 58, 40);

    doc.setDrawColor(...primary);
    doc.setLineWidth(1.5);
    doc.line(margin, 52, pageW - margin, 52);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, 54, pageW - margin, 54);

    // Info box
    let y = 60;
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 14, 3, 3, 'F');
    doc.setDrawColor(230, 230, 230);
    doc.roundedRect(margin, y, contentW, 14, 3, 3, 'S');

    doc.setTextColor(...dark);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMASI LAPORAN', margin + 8, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    const now = new Date();
    doc.text(`Dicetak: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin + 8, y + 11);
    doc.text(`Status: ${isProfit ? 'LABA' : 'RUGI'}`, margin + contentW / 2, y + 11);

    y += 22;

    // ===== PENDAPATAN TABLE =====
    doc.setFillColor(...dark);
    doc.roundedRect(margin, y, contentW, 10, 2, 2, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('PENDAPATAN', margin + 6, y + 7);
    doc.setFontSize(9);
    doc.text(`Rp ${(data.total_pendapatan || 0).toLocaleString('id-ID')}`, pageW - margin - 6, y + 7, { align: 'right' });
    y += 12;

    if (data.pendapatan && data.pendapatan.length > 0) {
      const pdRows = data.pendapatan.map(p => [
        `${p.kode_akun} - ${p.nama_akun}`,
        (p.saldo || 0).toLocaleString('id-ID'),
      ]);
      pdRows.push(['TOTAL PENDAPATAN', (data.total_pendapatan || 0).toLocaleString('id-ID')]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Kode & Nama Akun', 'Saldo (Rp)']],
        body: pdRows,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
        headStyles: { fillColor: [16, 185, 129], textColor: [...white], fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
        didParseCell: function(td) {
          if (td.row.index === pdRows.length - 1) {
            td.cell.styles.fillColor = [209, 250, 229];
            td.cell.styles.textColor = [...dark];
            td.cell.styles.fontStyle = 'bold';
            td.cell.styles.lineWidth = 0.8;
            td.cell.styles.lineColor = [16, 185, 129];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // ===== BEBAN TABLE =====
    doc.setFillColor(...dark);
    doc.roundedRect(margin, y, contentW, 10, 2, 2, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('BEBAN', margin + 6, y + 7);
    doc.setFontSize(9);
    doc.text(`Rp ${(data.total_beban || 0).toLocaleString('id-ID')}`, pageW - margin - 6, y + 7, { align: 'right' });
    y += 12;

    if (data.beban && data.beban.length > 0) {
      const bbRows = data.beban.map(b => [
        `${b.kode_akun} - ${b.nama_akun}`,
        (b.saldo || 0).toLocaleString('id-ID'),
      ]);
      bbRows.push(['TOTAL BEBAN', (data.total_beban || 0).toLocaleString('id-ID')]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Kode & Nama Akun', 'Saldo (Rp)']],
        body: bbRows,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
        headStyles: { fillColor: [239, 68, 68], textColor: [...white], fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: [254, 242, 242] },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
        didParseCell: function(td) {
          if (td.row.index === bbRows.length - 1) {
            td.cell.styles.fillColor = [254, 226, 226];
            td.cell.styles.textColor = [...dark];
            td.cell.styles.fontStyle = 'bold';
            td.cell.styles.lineWidth = 0.8;
            td.cell.styles.lineColor = [239, 68, 68];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // ===== LABA RUGI SUMMARY =====
    if (y > pageH - 60) {
      doc.addPage();
      y = margin + 10;
    }

    doc.setFillColor(isProfit ? 240 : 254, isProfit ? 253 : 242, isProfit ? 244 : 242);
    doc.roundedRect(margin, y, contentW, 36, 3, 3, 'F');
    doc.setDrawColor(...(isProfit ? [16, 185, 129] : [239, 68, 68]));
    doc.setLineWidth(1);
    doc.roundedRect(margin, y, contentW, 36, 3, 3, 'S');

    doc.setTextColor(...(isProfit ? [6, 95, 70] : [153, 27, 27]));
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(isProfit ? 'LABA BERSIH' : 'RUGI BERSIH', margin + 10, y + 14);

    doc.setTextColor(...gray);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Pendapatan Rp ${(data.total_pendapatan || 0).toLocaleString('id-ID')} - Beban Rp ${(data.total_beban || 0).toLocaleString('id-ID')}`, margin + 10, y + 22);

    doc.setTextColor(...(isProfit ? [5, 150, 105] : [220, 38, 38]));
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`Rp ${Math.abs(data.laba_rugi || 0).toLocaleString('id-ID')}`, pageW - margin - 10, y + 22, { align: 'right' });

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(...primary);
      doc.setLineWidth(1);
      doc.line(margin, pageH - 15, pageW - margin, pageH - 15);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(margin, pageH - 14, pageW - margin, pageH - 14);

      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text('Layanan Synexa - Sistem Informasi Armada', margin, pageH - 9);
      doc.text(`Dicetak: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, pageH - 5);
      doc.setFont('helvetica', 'normal');
      doc.text(`Halaman ${i} dari ${pageCount}`, pageW - margin, pageH - 5, { align: 'right' });
    }

    doc.save(`Laba_Rugi_${tglMulai}_sd_${tglSelesai}.pdf`);
  };

  if (loading) return <div className="page-loading">Memuat laba rugi...</div>;
  if (!data) return <div className="page-error">Gagal memuat data</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Laba Rugi</h2>
        <p className="page-desc">Laporan pendapatan dan beban selama periode tertentu</p>
      </div>

      <div className="page-header-row" style={{ marginBottom: 16 }}>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={generatePDF}>
            <Printer size={16} /><span>Export PDF</span>
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Periode:</span>
          <input type="date" value={tglMulai} onChange={e => setTglMulai(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }} />
          <span style={{ color: 'var(--gray-400)' }}>-</span>
          <input type="date" value={tglSelesai} onChange={e => setTglSelesai(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="table-wrapper">
          <h3 style={{ padding: '14px 16px', fontSize: '0.9rem', borderBottom: '2px solid #10b981', color: 'var(--gray-600)' }}>PENDAPATAN</h3>
          <table className="data-table">
            <tbody>
              {data.pendapatan?.map((p, i) => (
                <tr key={i}><td style={{ fontSize: '0.85rem' }}>{p.kode_akun} - {p.nama_akun}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{rupiah(p.saldo)}</td></tr>
              ))}
              <tr style={{ background: 'var(--gray-50)' }}>
                <td style={{ fontWeight: 700 }}>Total Pendapatan</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>{rupiah(data.total_pendapatan)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="table-wrapper">
          <h3 style={{ padding: '14px 16px', fontSize: '0.9rem', borderBottom: '2px solid #ef4444', color: 'var(--gray-600)' }}>BEBAN</h3>
          <table className="data-table">
            <tbody>
              {data.beban?.map((b, i) => (
                <tr key={i}><td style={{ fontSize: '0.85rem' }}>{b.kode_akun} - {b.nama_akun}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{rupiah(b.saldo)}</td></tr>
              ))}
              <tr style={{ background: 'var(--gray-50)' }}>
                <td style={{ fontWeight: 700 }}>Total Beban</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{rupiah(data.total_beban)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 20, padding: '16px 20px', borderRadius: 'var(--radius)', border: '2px solid',
        borderColor: data.laba_rugi >= 0 ? '#10b981' : '#ef4444',
        background: data.laba_rugi >= 0 ? '#f0fdf4' : '#fef2f2',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h4 style={{ fontSize: '1.1rem', color: data.laba_rugi >= 0 ? '#065f46' : '#991b1b' }}>
            {data.laba_rugi >= 0 ? '📈 LABA BERSIH' : '📉 RUGI BERSIH'}
          </h4>
          <p style={{ fontSize: '0.85rem', color: 'var(--gray-400)' }}>
            Pendapatan {rupiah(data.total_pendapatan)} - Beban {rupiah(data.total_beban)}
          </p>
        </div>
        <span style={{ fontSize: '1.5rem', fontWeight: 800, color: data.laba_rugi >= 0 ? '#059669' : '#dc2626' }}>
          {rupiah(Math.abs(data.laba_rugi))}
        </span>
      </div>
    </div>
  );
}
